import LBox from "@components/LBox";
import LPressable from "@components/LPressable";
import LText from "@components/LText";
import LArtwork from "@components/LArtwork";
import LGradient from "@components/LGradient";
import { UserProfileModalSWMSheet } from "@components/modals/UserProfileModalSWM";
import { levelImages, levelBadgeConfig, DEFAULT_BADGE_CONFIG } from "@components/Account/You/LevelBadge";
import { useArtistCommunity } from "@hooks/trpc/useArtistCommunity";
import { theme, fonts } from "@theme";
import { useLocalSearchParams } from "expo-router";
import { Image } from "react-native";
import { ActivityIndicator, StyleSheet } from "react-native";
import Animated, { FadeInUp, FadeOutUp, LinearTransition } from "react-native-reanimated";
import { useBottomSheetManager } from "react-native-bottom-sheet-stack";

type FanRowProps = {
  userId: string;
  rank: number;
  name: string;
  image: string | null;
  fanScore: number;
  levelName: string;
  shadowColor: string;
  isCurrentUser?: boolean;
  movement: number;
  frozen?: boolean;
};

const MovementIndicator = ({ movement }: { movement: number }) => {
  if (movement > 0) {
    return (
      <LText textAlign="right" color="artistGreen" style={styles.movementText}>
        {"\u25B2"} {movement}
      </LText>
    );
  }
  if (movement < 0) {
    return (
      <LText textAlign="right" color="danger" style={styles.movementText}>
        {"\u25BC"} {Math.abs(movement)}
      </LText>
    );
  }
  return null;
  // return (
  //   <LText textAlign="right" color="lightGreyText" style={styles.movementText}>
  //     {"—"} {movement}
  //   </LText>
  // );
};

// Fan score badge — level icon + points on gradient bg (same style as You screen)
const FanScoreBadge = ({ levelName, fanScore }: { levelName: string; fanScore: number }) => {
  const key = levelName.toLowerCase();
  const config = levelBadgeConfig[key] ?? DEFAULT_BADGE_CONFIG;
  const icon = levelImages[key];
  return (
    <LGradient
      colors={[config.bgTop, "#101010"]}
      gradientDirection="vertical"
      flexDirection="row"
      alignItems="center"
      gap="s1"
      paddingHorizontal="s1_5"
      paddingVertical="s1"
      borderRadius="max"
      style={{
        borderWidth: 0.5,
        borderColor: config.border,
      }}
    >
      {icon && <Image source={icon} style={{ width: 14, height: 14 }} />}
      <LText
        variant="Caption/C2/SemiBold"
        gradientColours={[config.textLeft, config.textRight]}
        gradientDirection="horizontal"
      >
        {Math.round(fanScore)} pts
      </LText>
    </LGradient>
  );
};

// Frozen badge — grey background, grayscale level icon, no score
const FrozenBadge = ({ levelName }: { levelName: string }) => {
  const icon = levelImages[levelName.toLowerCase()];
  return (
    <LBox
      flexDirection="row"
      alignItems="center"
      paddingHorizontal="s1_5"
      paddingVertical="s1"
      borderRadius="max"
      style={{
        backgroundColor: "#1A1A1A",
        borderWidth: 0.5,
        borderColor: "#40404040",
      }}
    >
      {icon && <Image source={icon} style={{ width: 14, height: 14, tintColor: "#666666" }} />}
    </LBox>
  );
};

const FanRow = ({
  userId,
  rank,
  name,
  image,
  fanScore,
  levelName,
  shadowColor,
  isCurrentUser,
  movement,
  frozen,
}: FanRowProps) => {
  const bottomSheetManager = useBottomSheetManager();

  const handlePress = () => {
    bottomSheetManager.open(
      <UserProfileModalSWMSheet userId={userId} name={name} image={image} />,
      {
        mode: "push",
      },
    );
  };

  const borderColor = frozen ? "#404040" : shadowColor;

  return (
    <LPressable
      onPress={handlePress}
      sentryEventName="community_fan_row_pressed"
    >
      <LBox flexDirection="row" alignItems="center" gap="s4">
        {/* Rank */}
        <LText color="textPrimary" variant="bodyM" numberOfLines={1}>
          {rank}
        </LText>

        <LBox flex={1} flexDirection="row" alignItems="center" gap="s3">
          {/* Avatar with level-colored border */}
          <LBox
            style={{
              width: 32,
              height: 32,
              borderRadius: theme.borderRadii["m"],
              borderWidth: 1.5,
              borderColor,
              overflow: "hidden",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <LArtwork
              imageSize="small"
              priority="high"
              size={32}
              url={image}
              userAvatar
            />
          </LBox>

          {/* Name */}
          <LText variant="Body/B1/Medium" numberOfLines={1} style={{ flexShrink: 1 }}>
            {isCurrentUser ? "Me" : name}
          </LText>

          {/* Score Badge */}
          {frozen ? (
            <FrozenBadge levelName={levelName} />
          ) : (
            <FanScoreBadge levelName={levelName} fanScore={fanScore} />
          )}
        </LBox>

        {/* Movement */}
        <LBox alignItems="flex-end" style={styles.movementContainer}>
          {!frozen && <MovementIndicator movement={movement} />}
        </LBox>
      </LBox>
    </LPressable>
  );
};

const Community = ({ artistId: artistIdProp }: { artistId?: string } = {}) => {
  // On the artist profile screens the id comes from the route; when embedded
  // elsewhere (e.g. the Pro dashboard) it is passed explicitly as a prop.
  const params = useLocalSearchParams<{ artistId: string }>();
  const artistId = artistIdProp ?? params.artistId;
  const { data, isLoading } = useArtistCommunity(
    { artistId: artistId ?? "" },
    { enabled: !!artistId },
  );

  if (isLoading) {
    return (
      <LBox flex={1} alignItems="center">
        <ActivityIndicator color={theme.colors.white} />
      </LBox>
    );
  }

  if (!data || (data.topFans.length === 0 && !data.me)) {
    return (
      <LBox
        flex={1}
        alignSelf="center"
        style={{ width: "70%", paddingTop: "30%" }}
      >
        <LText color="lightGreyText" textAlign="center">
          No community data yet. Start streaming to see your ranking!
        </LText>
      </LBox>
    );
  }

  const meIsInTopFans = data.me
    ? data.topFans.some((f) => f.userId === data.me?.userId)
    : false;

  return (
    <LBox paddingHorizontal="s4">
      {/* Top Fans */}
      <LBox gap="s3" layout={LinearTransition}>
        {data.topFans.map((fan) => (
          <Animated.View
            key={fan.userId}
            layout={LinearTransition.duration(300)}
            entering={FadeInUp.duration(200)}
            exiting={FadeOutUp.duration(200)}
          >
            <FanRow
              userId={fan.userId}
              rank={fan.rank}
              name={fan.name}
              image={fan.image}
              fanScore={fan.fanScore}
              levelName={fan.levelName}
              shadowColor={fan.shadowColor}
              isCurrentUser={fan.userId === data.me?.userId}
              movement={fan.movement}
              frozen={fan.frozen}
            />
          </Animated.View>
        ))}
      </LBox>

      {/* Current user card (if outside top 5) */}
      {data.me && !meIsInTopFans && (
        <Animated.View layout={LinearTransition.duration(300)}>
          <LBox paddingHorizontal="s4" paddingVertical="s1">
            <LText color="lightGreyText" fontSize={12} textAlign="center">
              {"\u2022 \u2022 \u2022"}
            </LText>
          </LBox>
          <FanRow
            userId={data.me.userId}
            rank={data.me.rank}
            name={data.me.name}
            image={data.me.image}
            fanScore={data.me.fanScore}
            levelName={data.me.levelName}
            shadowColor={data.me.shadowColor}
            isCurrentUser
            movement={data.me.movement}
            frozen={data.me.frozen}
          />
        </Animated.View>
      )}
    </LBox>
  );
};

const styles = StyleSheet.create({
  movementText: {
    fontFamily: fonts.semiBold,
    fontSize: 13,
  },
  movementContainer: {
    minWidth: 40,
  },
});

export default Community;
